import { PageRoute } from '../constants/page-route.const';
import { solutions, SolutionVariant } from '../pages/solutions/solutions-types';
import { Breadcrumb } from './build-breadcrumb-json-ld';
import { isKnownMarketingPath } from './is-known-marketing-path';

export type PageSeo = {
  breadcrumbs?: Breadcrumb[];
  description: string;
  includeJsonLdHomepage?: boolean;
  robots?: string;
  title: string;
};

const HOME_DESCRIPTION =
  'CapQuest is the equity management platform for startups: cap table management, ESOPs, scenario modeling and investor relations, all in one place.';

const clip = (text: string, max = 158): string =>
  text.length <= max ? text : `${text.slice(0, max - 1)}…`;

/**
 * A solution page's tags default to its on-page title and hero copy, which keeps the two in sync
 * for free. Where marketing writes the search listing separately from the page headline, the
 * override wins — the keywords that earn the click are not always the words that read best as an H1.
 */
const SOLUTION_SEO_OVERRIDES: Partial<
  Record<SolutionVariant, { description: string; title: string }>
> = {
  [SolutionVariant.SIMPLIFIED_CAP_TABLE]: {
    title: 'Cap Table Software for Private Companies | CapQuest',
    description:
      'CapQuest cap table software gives startups one live, fully diluted cap table and digital share register, with full transaction history in any currency.',
  },
  [SolutionVariant.EQUITY_PLAN_AUTOMATION]: {
    title: 'ESOP Management Software & Equity Plan Automation | CapQuest',
    description:
      'Run your employee share ownership plan with CapQuest: stock options, BSPCE, RSUs and phantom shares, automated vesting schedules and easy employee onboarding.',
  },
  [SolutionVariant.ENHANCED_INVESTOR_RELATIONS]: {
    title: 'Investor Relations Software & Investor Portal | CapQuest',
    description:
      'Keep investors informed with CapQuest: an investor portal with personalized dashboards, automated investor updates, KPI reporting and digital onboarding.',
  },
  [SolutionVariant.OPTIMIZED_FUNDRAISING_INSTRUMENTS]: {
    title: 'SAFE, Convertible Note & Share Class Management | CapQuest',
    description:
      'Manage share classes, SAFEs, convertible notes, BSA AIR and venture debt on one cap table. Track valuation caps, discounts and conversions automatically.',
  },
};

const solutionSeo = (slug: string): null | PageSeo => {
  const variant = Object.values(SolutionVariant).find((v) => v === slug);
  if (!variant) {
    return null;
  }
  const sol = solutions[variant];
  const override = SOLUTION_SEO_OVERRIDES[variant];
  return {
    title: override?.title ?? `${sol.title} | CapQuest`,
    description: clip(override?.description ?? sol.description),
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Solutions', path: '/solutions' },
      { name: sol.title, path: `/solutions/${slug}` },
    ],
  };
};

const termsSeo = (pathname: string): null | PageSeo => {
  const map: Record<string, { description: string; title: string }> = {
    [PageRoute.CONFIDENTIALITY]: {
      title: 'Confidentiality notice | CapQuest',
      description:
        'Confidentiality notice for visitors and users of CapQuest marketing pages and materials.',
    },
    [PageRoute.COOKIE]: {
      title: 'Cookie policy | CapQuest',
      description:
        'How CapQuest uses cookies and similar technologies on capquest.io and related sites.',
    },
    [PageRoute.DPA]: {
      title: 'Data processing agreement | CapQuest',
      description: 'Data processing terms for customers using CapQuest services.',
    },
    [PageRoute.PRIVACY]: {
      title: 'Privacy policy | CapQuest',
      description: 'How CapQuest collects, uses, and protects personal data.',
    },
    [PageRoute.TERMS]: {
      title: 'Terms of service | CapQuest',
      description: 'Terms governing use of the CapQuest website and application.',
    },
    [PageRoute.TERMS_BASE]: {
      title: 'Terms of service | CapQuest',
      description: 'Terms governing use of the CapQuest website and application.',
    },
  };

  const entry = map[pathname];
  if (!entry) {
    return null;
  }

  return {
    title: entry.title,
    description: entry.description,
  };
};

export const resolvePageSeo = (pathname: string): PageSeo => {
  const normalized =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;

  if (!isKnownMarketingPath(normalized)) {
    return {
      title: 'Page not found | CapQuest',
      description:
        'The page you requested could not be found. Return to the CapQuest homepage or contact support.',
      robots: 'noindex, follow',
    };
  }

  if (normalized === '/' || normalized === '') {
    return {
      title: 'Cap Table & Equity Management Software | CapQuest',
      description: HOME_DESCRIPTION,
      includeJsonLdHomepage: true,
    };
  }

  if (normalized === '/about') {
    return {
      title: 'About CapQuest | Cap Table Management Company',
      description:
        'Meet CapQuest, the cap table management company with offices at Station F Paris and DIFC Dubai, making equity simple for startups, teams and investors.',
    };
  }

  if (normalized === '/perks') {
    return {
      title: 'Startup Perks & Partner Offers | CapQuest',
      description:
        'Exclusive perks for CapQuest customers: partner offers on financing, legal and more, alongside cap table management that grows with your startup.',
    };
  }

  if (normalized === '/become-a-partner') {
    return {
      title: 'Cap Table & ESOP Partner Program | CapQuest',
      description:
        'Join the CapQuest partner program. Offer clients modern cap table services and ESOP advisory tools, and earn new revenue as a legal, accounting or VC partner.',
    };
  }

  if (normalized === '/solutions') {
    return {
      title: 'Solutions | CapQuest',
      description:
        'Explore CapQuest solutions for cap tables, equity automation, scenario modeling, fundraising instruments, and investor relations.',
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'Solutions', path: '/solutions' },
      ],
    };
  }

  if (normalized.startsWith('/solutions/')) {
    const slug = normalized.slice('/solutions/'.length);
    const fromSolution = solutionSeo(slug);
    if (fromSolution) {
      return fromSolution;
    }
  }

  const fromTerms = termsSeo(normalized);
  if (fromTerms) {
    return fromTerms;
  }

  return {
    title: 'CapQuest',
    description: HOME_DESCRIPTION,
  };
};
